/* eslint-disable max-lines */
import { useSidebarPanel } from '@/components/layout/Navigation/sidebar-panel';
import {
  isShortcutsDisabledAtom,
  isShortcutsSheetOpenAtom,
} from '@/components/layout/ShortcutsHelper/shortcutsAtom';
import {
  useIsNewSessionListEnabled,
  useIsProjectsEnabled,
  useIsRibbonSidebarEnabled,
} from '@/features/flags';
import { useSettingsModal } from '@/features/settings/hooks/useSettingsModal';
import { trackShortcutUsed } from '@/lib/analytics/scribe/general';
import {
  beginSessionCreateAttempt,
  markSessionCreateMode,
} from '@/lib/telemetry/sessionCreateTracking';
import { hotkeysList } from '@/lists/hotkeys';
import { ShortcutContext } from '@/models/analyticsScribeModel';
import { isTauri } from '@/tauri/utils';
import { pendingWorkHomeViewAtom } from '@heidi/agents/data';
import { useAtom, useSetAtom } from 'jotai/react';
import router from 'next/router';
import { startTransition, useEffect, useRef, useTransition } from 'react';
import { isHotkeyPressed, useHotkeys, useRecordHotkeys } from 'react-hotkeys-hook';
import { Key } from 'ts-key-enum';
import { useMutateCreateNewSession } from './mutation/useMutateCreateNewSession';
import { useNewSessionMutationPending } from './useNewSession';
import { useSidebarState } from './usePageShellState';

export const useSideNavHotkeys = () => {
  const [isShortcutsDisabled, setIsShortcutsDisabled] = useAtom(isShortcutsDisabledAtom);
  const { mutate: onNewSessionClick } = useMutateCreateNewSession();
  const isCreatingSession = useNewSessionMutationPending();
  const isCreatingSessionRef = useRef(false);
  const setIsShortcutsSheetOpen = useSetAtom(isShortcutsSheetOpenAtom);
  const [keys, { start, stop, isRecording }] = useRecordHotkeys();
  const [isPending] = useTransition();
  const { open: openSettingsModal } = useSettingsModal();

  const { toggle: toggleSidebar } = useSidebarState();
  const isNewSessionListEnabled = useIsNewSessionListEnabled();
  const isRibbonSidebarEnabled = useIsRibbonSidebarEnabled();
  const isProjectsEnabled = useIsProjectsEnabled();
  const setPendingWorkHomeView = useSetAtom(pendingWorkHomeViewAtom);
  const sessionsRoute = isNewSessionListEnabled ? '/scribe/sessions' : '/scribe';

  // Sidebar panel toggle (when new sidebar panel feature is enabled)
  const { togglePin: toggleSidebarPanel, isEnabled: isSidebarPanelEnabled } =
    useSidebarPanel();

  useHotkeys(
    hotkeysList.goToTemplateLibrary.hotkeys,
    () => {
      startTransition(() => {
        void router.push('/scribe/my-templates');
        trackShortcutUsed({
          shortcutName: ShortcutContext.GO_TO_TEMPLATE_LIBRARY,
        });
      });
    },
    {
      scopes: hotkeysList.goToTemplateLibrary.scopes,
      preventDefault: true,
      enabled: !isShortcutsDisabled,
    },
    [isShortcutsDisabled]
  );

  useHotkeys(
    hotkeysList.goToTeam?.hotkeys ?? [],
    () => {
      void router.push('/team');
      trackShortcutUsed({
        shortcutName: ShortcutContext.GO_TO_TEAM,
      });
    },
    {
      scopes: hotkeysList.goToTeam?.scopes,
      preventDefault: true,
      enabled: !isShortcutsDisabled,
    },
    [isShortcutsDisabled]
  );

  useHotkeys(
    hotkeysList.goToSettings?.hotkeys ?? [],
    () => {
      openSettingsModal(undefined, 'hotkey');
      trackShortcutUsed({
        shortcutName: ShortcutContext.GO_TO_ACCOUNT,
      });
    },
    {
      scopes: hotkeysList.goToSettings?.scopes,
      preventDefault: true,
      enabled: !isShortcutsDisabled,
    },
    [isShortcutsDisabled, openSettingsModal]
  );

  useHotkeys(
    hotkeysList.newSession.hotkeys,
    () => {
      startTransition(() => {
        // The ref as well as the pending state, matching `useNewSession.ts`'s
        // `isRoutingRef`: `isCreatingSession` is a render-time closure value, so
        // a held-key repeat can deliver two presses before the pending state
        // re-renders and both would read false.
        //
        // The whole handler returns, the shortcut-used event included: a press
        // that creates nothing is not shortcut usage.
        if (isCreatingSessionRef.current || isCreatingSession) {
          return;
        }
        isCreatingSessionRef.current = true;
        beginSessionCreateAttempt({ entryPoint: 'shortcut' });
        // Online only: the shortcut skips useNewSession's offline route, so an
        // offline Desktop press fails as network_unavailable.
        markSessionCreateMode('online');
        onNewSessionClick(
          { attempt: {} },
          {
            onSettled: () => {
              isCreatingSessionRef.current = false;
            },
          }
        );
        trackShortcutUsed({
          shortcutName: ShortcutContext.NEW_SESSION,
        });
      });
    },
    {
      scopes: hotkeysList.newSession.scopes,
      preventDefault: true,
      enabled: !isShortcutsDisabled,
    },
    [onNewSessionClick, isCreatingSession, isShortcutsDisabled]
  );

  useHotkeys(
    hotkeysList.openShortcutsHelper.hotkeys,
    () => {
      if (isHotkeyPressed('g')) {
        return;
      }
      startTransition(() => {
        setIsShortcutsSheetOpen((prev) => !prev);
        trackShortcutUsed({
          shortcutName: ShortcutContext.OPEN_SHORTCUTS_HELPER,
        });
      });
    },
    {
      enabled: !isRecording && !isShortcutsDisabled,
      preventDefault: true,
      scopes: hotkeysList.openShortcutsHelper.scopes,
    },
    [setIsShortcutsSheetOpen, isRecording, isShortcutsDisabled]
  );

  useHotkeys(
    hotkeysList.goToSessions.hotkeys,
    () => {
      startTransition(() => {
        void router.push(sessionsRoute);
        trackShortcutUsed({
          shortcutName: ShortcutContext.GO_TO_SESSIONS,
        });
      });
    },
    {
      scopes: hotkeysList.goToSessions.scopes,
      preventDefault: true,
      enabled: !isShortcutsDisabled,
    },
    [isShortcutsDisabled, sessionsRoute]
  );

  // Desktop: Tauri owns Cmd+Shift+H globally (floating overlay). Web: open Work.
  // Untracked: the `no-new-rematch-coupling` ratchet freezes new
  // `dispatch.analyticsScribe` call sites in this file, and `trackShortcutUsed`
  // has no `track()` equivalent yet. Matches useMakeAsWorkflowShortcut.
  useHotkeys(
    hotkeysList.summonHeidi.hotkeys,
    () => {
      startTransition(() => {
        setPendingWorkHomeView('home');
        void router.push('/scribe/chat/projects');
      });
    },
    {
      scopes: hotkeysList.summonHeidi.scopes,
      preventDefault: true,
      enabled: !isShortcutsDisabled && isProjectsEnabled && !isTauri(),
    },
    [isShortcutsDisabled, isProjectsEnabled, setPendingWorkHomeView]
  );

  useHotkeys(
    Key.Escape,
    () => {
      startTransition(() => {
        // focus out from activeElement
        if (
          (document.activeElement?.tagName === 'INPUT' ||
            document.activeElement?.tagName === 'TEXTAREA') &&
          document.activeElement &&
          'blur' in document.activeElement
        ) {
          (document.activeElement as HTMLElement).blur();
        }
      });
    },
    {
      enableOnFormTags: ['INPUT', 'TEXTAREA'],
    },
    []
  );

  useHotkeys(
    hotkeysList.openSideNav.hotkeys,
    () => {
      toggleSidebar();

      trackShortcutUsed({
        shortcutName: ShortcutContext.OPEN_SIDE_NAV,
      });
    },
    {
      preventDefault: true,
      enabled: !isShortcutsDisabled && !isRibbonSidebarEnabled,
    },
    [toggleSidebar, isShortcutsDisabled, isRibbonSidebarEnabled]
  );

  useHotkeys(
    hotkeysList.pinUnpinSidebarPanel.hotkeys,
    () => {
      toggleSidebarPanel();

      trackShortcutUsed({
        shortcutName: ShortcutContext.PIN_UNPIN_SIDEBAR_PANEL,
      });
    },
    {
      preventDefault: true,
      enabled: !isShortcutsDisabled && isSidebarPanelEnabled,
    },
    [toggleSidebarPanel, isShortcutsDisabled, isSidebarPanelEnabled]
  );

  // for shortcuts
  useEffect(() => {
    const handleFocusIn = () => {
      const activeElement = document.activeElement as HTMLElement | null;
      const hasGlobalShortcutDisableRegion = Boolean(
        document.querySelector('[data-disable-global-shortcuts]')
      );
      const shouldDisable =
        hasGlobalShortcutDisableRegion ||
        (activeElement?.hasAttribute('data-radix-collection-item') ?? false) ||
        Boolean(activeElement?.closest('[data-disable-global-shortcuts]'));
      setIsShortcutsDisabled((prev) => (prev === shouldDisable ? prev : shouldDisable));
    };
    window.addEventListener('focusin', handleFocusIn);
    return () => {
      window.removeEventListener('focusin', handleFocusIn);
    };
  }, [setIsShortcutsDisabled]);

  useHotkeys(
    hotkeysList.startRecordingKeyInputForRouteNavigation.hotkeys,
    () => {
      if (isHotkeyPressed('s')) return;
      start();
      setIsShortcutsDisabled(true);
      trackShortcutUsed({
        shortcutName: ShortcutContext.START_STOP_RECORDING,
      });
    },
    {
      enabled: !isRecording && !isShortcutsDisabled,
      preventDefault: true,
      keyup: true,
      keydown: false,
      scopes: hotkeysList.startRecordingKeyInputForRouteNavigation.scopes,
    },
    [setIsShortcutsDisabled, start, isRecording, isShortcutsDisabled]
  );

  // if any of navigation hotkeys is pressed go to the corresponding page and stop recording
  useEffect(() => {
    if (!isRecording || isPending) return;
    if (keys.size === 1) {
      startTransition(() => {
        switch (keys.keys().next().value) {
          case 's':
            void router.push(sessionsRoute);
            trackShortcutUsed({
              shortcutName: ShortcutContext.GO_TO_SESSIONS,
            });
            break;
          case 't':
            void router.push('/team');
            trackShortcutUsed({
              shortcutName: ShortcutContext.GO_TO_TEAM,
            });
            break;
          case 'a':
            openSettingsModal(undefined, 'hotkey');
            trackShortcutUsed({
              shortcutName: ShortcutContext.GO_TO_ACCOUNT,
            });

            break;
          case 'p':
            void router.push('/privacy');
            trackShortcutUsed({
              shortcutName: ShortcutContext.GO_TO_PREFERENCE,
            });
            break;
          case 'l':
            void router.push('/scribe/my-templates');
            trackShortcutUsed({
              shortcutName: ShortcutContext.GO_TO_TEMPLATE_LIBRARY,
            });
            break;
          case 'c':
            void router.push('/templates');
            trackShortcutUsed({
              shortcutName: ShortcutContext.GO_TO_COMMUNITY,
            });
            break;

          default:
            break;
        }
        setIsShortcutsDisabled(false);
        stop();
      });
    }
  }, [
    keys,
    stop,
    isRecording,
    isPending,
    setIsShortcutsDisabled,
    openSettingsModal,
    sessionsRoute,
  ]);
};
